// File: frontend/src/api/tagService.ts
// Contains functions for tag-related API calls.

import apiClient from './index'; // Import the configured axios instance
import { type TagPublic, type TagCreatePayload, type TagUpdatePayload } from '@/types'; // Import TagPublic type and payloads

/**
 * Fetches all available tags from the backend.
 * @returns A promise that resolves to an array of TagPublic objects.
 */
export const getAllTags = async (): Promise<TagPublic[]> => {
  // Added trailing slash to the collection endpoint path
  // This helps prevent 307 Temporary Redirects if the backend enforces it.
  const response = await apiClient.get<TagPublic[]>('/tags/');
  return response.data;
};

/**
 * Creates a new tag.
 * @param tagData - The data for the new tag.
 * @returns A promise that resolves to the created TagPublic object.
 */
export const createTag = async (tagData: TagCreatePayload): Promise<TagPublic> => {
  // POST to a collection endpoint (e.g., /tags) typically does not use a trailing slash.
  // This should match your backend API's route definition for creating a tag.
  const response = await apiClient.post<TagPublic>('/tags', tagData);
  return response.data;
};

/**
 * Updates an existing tag.
 * @param tagId - The ID of the tag to update.
 * @param tagData - The data to update the tag with.
 * @returns A promise that resolves to the updated TagPublic object.
 */
export const updateTag = async (tagId: number, tagData: TagUpdatePayload): Promise<TagPublic> => {
  // PUT to a specific resource (e.g., /tags/{tagId}) typically does not use a trailing slash.
  const response = await apiClient.put<TagPublic>(`/tags/${tagId}`, tagData);
  return response.data;
};

/**
 * Deletes a tag.
 * @param tagId - The ID of the tag to delete.
 * @returns A promise that resolves when the tag is deleted.
 */
export const deleteTag = async (tagId: number): Promise<void> => {
  // DELETE on a specific resource (e.g., /tags/{tagId}) typically does not use a trailing slash.
  await apiClient.delete(`/tags/${tagId}`);
};
